import { ndIsNaN, isEmpty } from '@/helpers/utilities';

const definedFiledsType = {
  // 'fullText
  fullText: 'FullText',
  // 'freeText
  freeText: 'FreeText',
  // #Manufacturer of the vehicle
  manufacturer: String,
  // #Model of the vehicle
  model: String,
  // #Image url of the vehicle
  images: String,
  // #Range of current price
  currentPrice: 'SearchParamsIntRange',
  // # Previous price
  previousPrice: 'SearchParamsIntRange',
  // #Registration date
  registrationDate: 'SearchParamsStringRange',
  // #Range of number of images
  numImages: 'SearchParamsIntRange',
  // #Vehicle status like: available (default), sold or reserved
  status: String,
  // #Condition of the vehicle
  condition: String,
  // #Variant of the vehicle
  variant: String,
  // #Type of the vehicle
  type: String,
  // #Body style of the vehicle
  bodyStyle: String,
  // #Range of number of images
  numDoors: 'SearchParamsIntRange',
  // #Range of number of images
  numSeats: 'SearchParamsIntRange',
  // #Date when vehicle was created, format yyyy-MM-dd HH:mm:ss, default now
  createdAt: 'SearchParamsStringRange',
  // #Is vehicle featured
  isFeatured: Boolean,
  // #Vehicle production year, eg. 2019
  productionYear: 'SearchParamsIntRange',
  // #Vehicle number owners, eg. 2
  numOwners: 'SearchParamsIntRange',
  // #Vehicle vin
  vin: String,
  // #Finance data
  finances: 'FinanceItemSearchInput',
  // #Vehicle odometer data in miles
  odometerMiles: 'SearchParamsIntRange',
  // #Vehicle odometer data in kilometers
  odometerKilometers: 'SearchParamsIntRange',
  // #NCAP score
  ncapOverall: 'SearchParamsIntRange',
  // #Generic colour of the vehicle
  genericColour: String,
  // #Interior colour of the vehicle
  interiorColour: String,
  // #Exterior colour of the vehicle
  exteriorColour: String,
  // #Location name
  locationName: String,
  // #Location country
  locationCountry: String,
  // #Location district
  locationDistrict: String,
  // #Location region
  locationRegion: String,
  // #Location town name
  locationTown: String,
  // #Location postcode
  locationPostcode: String,
  // #Location street
  locationStreet: String,
  // #Equipment label
  equipmentLabel: String,
  // #Lowest value of finances payment, if not provided, taken from finances data,
  // #payment field, lowest available value
  financesFrom: 'SearchParamsIntRange',
  // #Location distance
  distance: 'DistanceRange',
  // #Vehicle age range
  age: 'SearchParamsIntRange',
  // #Vehicle drive type
  driveType: String,
  // #Vehicle fuel type
  fuelType: String,
  // #Vehicle fuel type label
  fuelTypeLabel: String,
  // #Vehicle transmission generic
  transmissionType: String,
  // #Vehicle engine size data
  engineSize: 'SearchParamsIntRange',
  // #Vehicle registration number (VRM)
  registrationNumber: String,
  // #Vehicle registration year
  registrationYear: 'SearchParamsIntRange',
  // #Manufacturer vehicle identifier
  manufacturerId: String,
  // #Vehicle location identifier
  locationId: String,
  // #Vehicle engine description
  engineDescription: String,
  // #Vehicle trim
  trim: String,
  // Offer
  offer: String,
  // #Company hash
  companyHash: String,
  // #Group hash
  groupHash: String,
  // #Franchise hash
  franchiseHash: String,
  // #Location hash
  locationHash: String,
  // #Tax band
  taxBand: String,
  // #Insurance group
  insuranceGroup: String,
  // #Efficiency Mpg
  efficiencyMpg: 'SearchParamsIntRange',
  // #Efficiency Lp100km
  efficiencyLp100km: 'SearchParamsIntRange',
  // #Features
  features: String,
  // #EV Range data in miles
  rangeMiles: 'SearchParamsIntRange',
  // #EV Range data in kilometers
  rangeKilometers: 'SearchParamsIntRange',
  // #Vehicle cap id
  capId: String,
  // #Vehicle emission euro
  emissionEuro: String,
  // # Is franchise approved
  isFranchiseApproved: Boolean,
  // Wheelbase
  wheelbase: 'SearchParamsIntRange',
  // #Vehicle engine power
  emissionCo2: 'SearchParamsIntRange',
  // #vehicle stock
  isSharedStock: Boolean,
};

const parseIntVal = (val) => {
  const parsed = parseInt(val, 10);
  return ndIsNaN(parsed) ? 0 : parsed;
};

const parseIntRange = (val) => {
  if (!val) return null;

  const { from, to } = val;
  if (ndIsNaN(from) && ndIsNaN(to)) return {};

  const data = {};

  if (to) data.to = parseIntVal(to, 10);
  if (from) data.from = parseIntVal(from, 10);

  return data;
};

const parseStringRange = (val) => {
  if (!val) return null;

  const { from, to } = val;
  if ([undefined, null, ''].includes(from) && [undefined, null, ''].includes(to)) return {};

  const data = {};

  if (to) data.to = `${to}`;
  if (from) data.from = `${from}`;

  return data;
};

const parseDistance = (val) => {
  if (!val) return null;

  const { lat, lon, distance, unit } = val;
  if (ndIsNaN(lat)
    || ndIsNaN(lon)
    || ndIsNaN(distance)
    || !['miles', 'km'].includes(unit?.toLowerCase())
  ) return {};
  return {
    lat: Number(lat),
    lon: Number(lon),
    distance: Number(distance),
    unit,
  };
};

// "String", "SearchParamsIntRange", "Boolean"
// "FinanceItemSearchInput", "DistanceRange", "ManufacturerModelsInput"
const fieldTypeMapFunctions = {
  SearchParamsStringRange: (val) => parseStringRange(val),
  SearchParamsIntRange: (val) => parseIntRange(val),
  FinanceItemSearchInput: (val) => {
    const mapped = {};
    if ('annualMileage' in val) {
      mapped.annualMileage = parseIntVal(val.annualMileage, 10);
    }
    if ('deposit' in val) {
      mapped.deposit = parseIntVal(val.deposit, 10);
    }
    if ('payment' in val) {
      mapped.payment = parseIntRange(val.payment);
    }
    if ('productName' in val) {
      mapped.productName = String(val.productName);
    }
    if ('term' in val) {
      mapped.term = parseIntVal(val.term, 10);
    }
    if ('productType' in val) {
      mapped.productType = String(val.productType);
    }
    return mapped;
  },
  DistanceRange: (val) => parseDistance(val),
  FullText: (val) => val,
  FreeText: (val) => val,
};

// eslint-disable-next-line import/prefer-default-export
const mappingFunction = (searchParams) => {
  const result = {};

  Object.entries(searchParams).forEach(([key, value]) => {
    const type = definedFiledsType[key];
    if (!type) return;

    const func = fieldTypeMapFunctions[type];

    if (!func && Array.isArray(value)) {
      result[key] = value.map((n) => type(n));
      return;
    }

    if (!func) {
      result[key] = type(value);
      return;
    }

    if (Array.isArray(value)) {
      const values = value.reduce((memo, n) => {
        const v = func(n);
        return !isEmpty(v) ? [...memo, v] : [...memo];
      }, []);
      if (values.length) result[key] = [...values];
      return;
    }

    if (!isEmpty(func(value))) {
      result[key] = func(value);
    }
  });
  return result;
};

export default {
  mappingFunction,
};
